import { Field } from 'rc-field-form';
import React, { Children, useMemo } from 'react';
import { Form as TDForm } from 'tdesign-react';
import { isEmpty } from 'lodash';
import { produce } from 'immer';
import { isVoid } from '@/utils/common';

/**
 * @typedef {import("./type").Props} Props
 */

/**
 * @param {Props} props
 * @returns
 */
export default function FormItem({
  name,
  rules: propRules = [],
  label,
  initialValue,
  children,
  shouldUpdate,
  dependencies,
  valuePropName,
  ...others
}) {
  // 存在必填
  const hasRequired = useMemo(
    () => propRules?.findIndex((item) => item.required) > -1,
    [propRules],
  );

  const rules = useMemo(() => {
    if (hasRequired) {
      return produce(propRules, (draft) => {
        const requiredRule = draft.find((item) => item.required);
        if (isVoid(requiredRule.message)) {
          requiredRule.message = `${label}必填`;
        }
      });
    }

    return propRules;
  }, [propRules, label, hasRequired]);

  return (
    <Field
      name={name}
      rules={rules}
      shouldUpdate={shouldUpdate}
      dependencies={dependencies}
      initialValue={initialValue}
      valuePropName={valuePropName}
    >
      {(props, meta) => {
        const { errors } = meta;

        const itemProps = {};
        if (!isEmpty(errors)) {
          itemProps.status = 'error';
          itemProps.tips = errors[0];
        }
        const isSingleChild = Children.count(children) === 1 && React.isValidElement(children);
        let child = children;

        if (isSingleChild && !isVoid(name)) {
          const originOnChange = child.props.onChange;

          const _props = {
            ...props,
            onChange: (...args) => {
              props.onChange(...args);
              if (originOnChange) {
                originOnChange(...args);
              }
            },
          };

          // 防止td属性污染，仅用样式（仅认为单个节点是表单节点）, 如果没有name 则忽略
          child = <>{React.cloneElement(children, isVoid(name) ? null : _props)}</>;
        }

        return (
          <TDForm.FormItem
            requiredMark={hasRequired}
            label={label}
            showErrorMessage
            {...itemProps}
            {...others}
          >
            {child}
          </TDForm.FormItem>
        );
      }}
    </Field>
  );
}
